export interface WSThemeButtonConfig {
  readonly borderRadius?: string;
  readonly fontFamily?: string;
  readonly fontWeight?: string;
  readonly primaryBg?: string;
  readonly primaryBgHover?: string;
  readonly primaryBgActive?: string;
  readonly primaryColor?: string;
  readonly secondaryBorder?: string;
  readonly secondaryColor?: string;
  readonly secondaryHoverBg?: string;
  readonly secondaryActiveBg?: string;
  readonly tertiaryBorder?: string;
  readonly tertiaryColor?: string;
  readonly focusRingColor?: string;
  readonly disabledBg?: string;
  readonly disabledColor?: string;
}

export interface WSThemeLinkConfig {
  readonly color?: string;
  readonly colorHover?: string;
  readonly colorActive?: string;
}

export interface WSThemeInputConfig {
  readonly borderRadius?: string;
  readonly borderColor?: string;
  readonly borderColorFocus?: string;
  readonly bg?: string;
  readonly color?: string;
  readonly placeholderColor?: string;
  readonly fontFamily?: string;
  readonly focusRingColor?: string;
  readonly disabledBg?: string;
  readonly disabledColor?: string;
}

export interface WSThemeControlConfig {
  readonly borderColor?: string;
  readonly checkedBg?: string;
  readonly checkedBorder?: string;
  readonly activeBg?: string;
  readonly focusRingColor?: string;
  readonly labelColor?: string;
  readonly labelCheckedColor?: string;
  readonly disabledBg?: string;
  readonly disabledBorder?: string;
}

export interface WSThemeTextKindConfig {
  readonly fontSize?: string;
  readonly lineHeight?: string;
  readonly fontWeight?: string;
}

export interface WSThemeTextConfig {
  readonly fontFamily?: string;
  readonly color?: string;
  readonly display1?: WSThemeTextKindConfig;
  readonly display2?: WSThemeTextKindConfig;
  readonly heading1?: WSThemeTextKindConfig;
  readonly heading2?: WSThemeTextKindConfig;
  readonly heading3?: WSThemeTextKindConfig;
  readonly heading4?: WSThemeTextKindConfig;
  readonly heading5?: WSThemeTextKindConfig;
  readonly paragraph?: WSThemeTextKindConfig;
  readonly paragraphSm?: WSThemeTextKindConfig;
  readonly paragraphXs?: WSThemeTextKindConfig;
  readonly caption?: WSThemeTextKindConfig;
}

export interface WSThemeSelectConfig {
  readonly dropdownBg?: string;
  readonly dropdownBorderRadius?: string;
  readonly dropdownShadow?: string;
  readonly optionHoverBg?: string;
  readonly optionActiveBg?: string;
  readonly searchFontFamily?: string;
}

export interface WSThemeConfig {
  readonly colorPrimary?: string;
  readonly borderRadius?: string;
  readonly fontFamily?: string;
  readonly colorText?: string;
  readonly colorBorder?: string;
  readonly button?: WSThemeButtonConfig;
  readonly link?: WSThemeLinkConfig;
  readonly input?: WSThemeInputConfig;
  readonly control?: WSThemeControlConfig;
  readonly text?: WSThemeTextConfig;
  readonly select?: WSThemeSelectConfig;
}

// --- Token maps ---

const BUTTON_TOKEN_MAP: Record<string, string> = {
  borderRadius: "--ws-button-border-radius",
  fontFamily: "--ws-button-font-family",
  fontWeight: "--ws-button-font-weight",
  primaryBg: "--ws-button-primary-bg",
  primaryBgHover: "--ws-button-primary-bg-hover",
  primaryBgActive: "--ws-button-primary-bg-active",
  primaryColor: "--ws-button-primary-color",
  secondaryBorder: "--ws-button-secondary-border",
  secondaryColor: "--ws-button-secondary-color",
  secondaryHoverBg: "--ws-button-secondary-hover-bg",
  secondaryActiveBg: "--ws-button-secondary-active-bg",
  tertiaryBorder: "--ws-button-tertiary-border",
  tertiaryColor: "--ws-button-tertiary-color",
  focusRingColor: "--ws-button-focus-ring-color",
  disabledBg: "--ws-button-disabled-bg",
  disabledColor: "--ws-button-disabled-color"
};

const LINK_TOKEN_MAP: Record<string, string> = {
  color: "--ws-link-color",
  colorHover: "--ws-link-color-hover",
  colorActive: "--ws-link-color-active"
};

const INPUT_TOKEN_MAP: Record<string, string> = {
  borderRadius: "--ws-input-border-radius",
  borderColor: "--ws-input-border-color",
  borderColorFocus: "--ws-input-border-color-focus",
  bg: "--ws-input-bg",
  color: "--ws-input-color",
  placeholderColor: "--ws-input-placeholder-color",
  fontFamily: "--ws-input-font-family",
  focusRingColor: "--ws-input-focus-ring-color",
  disabledBg: "--ws-input-disabled-bg",
  disabledColor: "--ws-input-disabled-color"
};

const CONTROL_TOKEN_MAP: Record<string, string> = {
  borderColor: "--ws-control-border-color",
  checkedBg: "--ws-control-checked-bg",
  checkedBorder: "--ws-control-checked-border",
  activeBg: "--ws-control-active-bg",
  focusRingColor: "--ws-control-focus-ring-color",
  labelColor: "--ws-control-label-color",
  labelCheckedColor: "--ws-control-label-checked-color",
  disabledBg: "--ws-control-disabled-bg",
  disabledBorder: "--ws-control-disabled-border"
};

const TEXT_KIND_KEYS = [
  "display1",
  "display2",
  "heading1",
  "heading2",
  "heading3",
  "heading4",
  "heading5",
  "paragraph",
  "paragraphSm",
  "paragraphXs",
  "caption"
] as const;

const TEXT_KIND_PROP_MAP: Record<string, string> = {
  fontSize: "font-size",
  lineHeight: "line-height",
  fontWeight: "font-weight"
};

const SELECT_TOKEN_MAP: Record<string, string> = {
  dropdownBg: "--ws-select-dropdown-bg",
  dropdownBorderRadius: "--ws-select-dropdown-border-radius",
  dropdownShadow: "--ws-select-dropdown-shadow",
  optionHoverBg: "--ws-select-option-hover-bg",
  optionActiveBg: "--ws-select-option-active-bg",
  searchFontFamily: "--ws-select-search-font-family"
};

// --- Color utilities ---

const parseHex = (
  hex: string
): readonly [number, number, number] | undefined => {
  const match = hex.replace(/^#/, "").match(/^([0-9a-f]{6})$/i);
  if (!match) return undefined;
  const h = match[1];
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16)
  ];
};

const clampByte = (n: number): number =>
  Math.min(255, Math.max(0, Math.round(n)));

const rgbToHex = (r: number, g: number, b: number): string =>
  "#" +
  [r, g, b]
    .map((c) => clampByte(c).toString(16).padStart(2, "0"))
    .join("");

export const darkenHex = (
  hex: string,
  amount: number
): string | undefined => {
  const rgb = parseHex(hex);
  if (!rgb) return undefined;
  const factor = 1 - amount;
  return rgbToHex(rgb[0] * factor, rgb[1] * factor, rgb[2] * factor);
};

export const lightenHex = (
  hex: string,
  amount: number
): string | undefined => {
  const rgb = parseHex(hex);
  if (!rgb) return undefined;
  return rgbToHex(
    rgb[0] + (255 - rgb[0]) * amount,
    rgb[1] + (255 - rgb[1]) * amount,
    rgb[2] + (255 - rgb[2]) * amount
  );
};

// --- Cascade: global → component tokens ---
// Each entry: [cssVarName, configPath that provides the value]
// Only applied when the component-level key is NOT explicitly set.

type CascadeRule = readonly [string, (config: WSThemeConfig) => string | undefined];

const CASCADE_RULES: ReadonlyArray<CascadeRule> = [
  // colorPrimary → button, link, input, control (direct)
  ["--ws-button-primary-bg", (c) => c.colorPrimary],
  ["--ws-button-secondary-border", (c) => c.colorPrimary],
  ["--ws-button-secondary-color", (c) => c.colorPrimary],
  ["--ws-link-color", (c) => c.colorPrimary],
  ["--ws-input-border-color-focus", (c) => c.colorPrimary],
  ["--ws-control-checked-bg", (c) => c.colorPrimary],
  ["--ws-control-checked-border", (c) => c.colorPrimary],

  // colorPrimary → derived button hover/active/focus states
  [
    "--ws-button-primary-bg-hover",
    (c) => (c.colorPrimary ? darkenHex(c.colorPrimary, 0.2) : undefined)
  ],
  [
    "--ws-button-primary-bg-active",
    (c) => (c.colorPrimary ? darkenHex(c.colorPrimary, 0.45) : undefined)
  ],
  [
    "--ws-button-focus-ring-color",
    (c) => (c.colorPrimary ? lightenHex(c.colorPrimary, 0.8) : undefined)
  ],
  [
    "--ws-button-secondary-hover-bg",
    (c) => (c.colorPrimary ? lightenHex(c.colorPrimary, 0.95) : undefined)
  ],
  [
    "--ws-button-secondary-active-bg",
    (c) => (c.colorPrimary ? lightenHex(c.colorPrimary, 0.9) : undefined)
  ],

  // colorPrimary → derived link hover/active
  [
    "--ws-link-color-hover",
    (c) => (c.colorPrimary ? darkenHex(c.colorPrimary, 0.2) : undefined)
  ],
  [
    "--ws-link-color-active",
    (c) => (c.colorPrimary ? darkenHex(c.colorPrimary, 0.45) : undefined)
  ],

  // colorPrimary → focus rings (input, control)
  [
    "--ws-input-focus-ring-color",
    (c) => (c.colorPrimary ? lightenHex(c.colorPrimary, 0.8) : undefined)
  ],
  [
    "--ws-control-focus-ring-color",
    (c) => (c.colorPrimary ? lightenHex(c.colorPrimary, 0.8) : undefined)
  ],

  // colorPrimary → control active background
  [
    "--ws-control-active-bg",
    (c) => (c.colorPrimary ? lightenHex(c.colorPrimary, 0.95) : undefined)
  ],

  // colorPrimary → tinted background (used by pill light variants, etc.)
  [
    "--ws-color-primary-light",
    (c) => (c.colorPrimary ? lightenHex(c.colorPrimary, 0.9) : undefined)
  ],

  // borderRadius → button, input, select
  ["--ws-button-border-radius", (c) => c.borderRadius],
  ["--ws-input-border-radius", (c) => c.borderRadius],
  ["--ws-select-dropdown-border-radius", (c) => c.borderRadius],

  // fontFamily → button, input, text, select
  ["--ws-button-font-family", (c) => c.fontFamily],
  ["--ws-input-font-family", (c) => c.fontFamily],
  ["--ws-text-font-family", (c) => c.fontFamily],
  ["--ws-select-search-font-family", (c) => c.fontFamily],

  // colorText → text, control label
  ["--ws-text-color", (c) => c.colorText],
  ["--ws-control-label-checked-color", (c) => c.colorText],

  // colorBorder → input
  ["--ws-input-border-color", (c) => c.colorBorder]
];

// --- Helpers ---

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const mapEntries = (
  source: any,
  tokenMap: Record<string, string>
): ReadonlyArray<readonly [string, string]> => {
  if (!source) return [];

  return Object.entries(tokenMap).flatMap(([key, cssVar]) => {
    const value = source[key];
    return typeof value === "string" ? [[cssVar, value] as const] : [];
  });
};

const mapTextKindEntries = (
  text: WSThemeTextConfig | undefined
): ReadonlyArray<readonly [string, string]> => {
  if (!text) return [];

  return TEXT_KIND_KEYS.flatMap((kind) => {
    const kindConfig = text[kind];
    if (!kindConfig) return [];

    return Object.entries(TEXT_KIND_PROP_MAP).flatMap(([prop, cssSuffix]) => {
      const value = kindConfig[prop as keyof WSThemeTextKindConfig];
      return typeof value === "string"
        ? [[`--ws-text-${kind}-${cssSuffix}`, value] as const]
        : [];
    });
  });
};

const buildExplicitTokens = (
  config: WSThemeConfig
): ReadonlyArray<readonly [string, string]> => [
  ...mapEntries(config, {
    colorPrimary: "--ws-color-primary",
    borderRadius: "--ws-border-radius",
    fontFamily: "--ws-font-family",
    colorText: "--ws-color-text",
    colorBorder: "--ws-color-border"
  }),
  ...mapEntries(config.button, BUTTON_TOKEN_MAP),
  ...mapEntries(config.link, LINK_TOKEN_MAP),
  ...mapEntries(config.input, INPUT_TOKEN_MAP),
  ...mapEntries(config.control, CONTROL_TOKEN_MAP),
  ...mapEntries(config.text, {
    fontFamily: "--ws-text-font-family",
    color: "--ws-text-color"
  }),
  ...mapTextKindEntries(config.text),
  ...mapEntries(config.select, SELECT_TOKEN_MAP)
];

const buildCascadedTokens = (
  config: WSThemeConfig,
  explicitVars: ReadonlySet<string>
): ReadonlyArray<readonly [string, string]> =>
  CASCADE_RULES.flatMap(([cssVar, resolver]) => {
    if (explicitVars.has(cssVar)) return [];
    const value = resolver(config);
    return typeof value === "string" ? [[cssVar, value] as const] : [];
  });

/**
 * Converts a WSThemeConfig into an array of [cssVarName, value] pairs.
 * Global tokens automatically cascade to component tokens unless
 * the component token is explicitly set in the config.
 */
export const buildThemeTokens = (
  config: WSThemeConfig
): ReadonlyArray<readonly [string, string]> => {
  const explicit = buildExplicitTokens(config);
  const explicitVars = new Set(explicit.map(([name]) => name));
  const cascaded = buildCascadedTokens(config, explicitVars);
  return [...explicit, ...cascaded];
};

/**
 * Applies a WSThemeConfig to a target element by setting CSS custom properties.
 * Defaults to document.documentElement (:root).
 */
export const applyWSTheme = (
  config: WSThemeConfig,
  target: HTMLElement = document.documentElement
): void => {
  buildThemeTokens(config).forEach(([name, value]) => {
    target.style.setProperty(name, value);
  });
};

/**
 * Removes all WS theme overrides from a target element.
 * Defaults to document.documentElement (:root).
 */
export const removeWSTheme = (
  config: WSThemeConfig,
  target: HTMLElement = document.documentElement
): void => {
  buildThemeTokens(config).forEach(([name]) => {
    target.style.removeProperty(name);
  });
};
